import { memo } from "react";
import { useNavigation } from "react-router-dom";

/**
 * Contract for tooling that must tell "the router resolved a real route" from
 * "the router gave up", without racing a timeout against a console log.
 */
export const ROUTE_STATE_ATTRIBUTE = "data-redo-route-state";

/**
 * `pending` means the router is still working, so no verdict is available yet.
 * `error` is a non-404 router error, which says nothing about the route.
 */
export type RouteState = "resolved" | "not-found" | "error" | "pending";

export const RouteStateMarker = memo(function RouteStateMarker({
  state,
  path,
}: {
  state: RouteState;
  path?: string;
}) {
  return (
    <div
      {...{ [ROUTE_STATE_ATTRIBUTE]: state }}
      data-redo-route-path={path}
      hidden
    />
  );
});

// Only `idle` is resolved: the tree renders mid-chain for a route about to
// redirect, and while a lazy element loads. Subscribing here rather than in
// RouterRoot keeps that component's inline context values from losing identity.
export const RouteStateReporter = memo(function RouteStateReporter() {
  const navigation = useNavigation();
  return (
    <RouteStateMarker
      state={navigation.state === "idle" ? "resolved" : "pending"}
    />
  );
});
